import { OperationType, WaypointType } from 'src/graphql/generated/graphql.generated';
import { RequestLocationConfig } from '../card/card-shared/components/request-location-form/request-location-form.component';
import {
  PanelConfig,
  RequestFormPanels,
} from '../card/request/components/request-page/request-page.component';

export interface OperationTypeConfigRequest {
  initialDestination: WaypointType;
  targetDestination?: WaypointType;
  locations: RequestLocationConfig[];
  formPanels: { [fe in RequestFormPanels]: PanelConfig };
}

interface OperationTypeConfigBase {
  name: string;
  abbreviation: string;
  key: string;
  icon?: string;
  // If you can create a request for an operation, configure it here
  request?: OperationTypeConfigRequest;
}

export enum NonOperationType {
  RequestorSpecialtyTeam = 'RequestorSpecialtyTeam',
}
export type OperationTypeConfig = OperationTypeConfigBase & {
  type: OperationType | NonOperationType;
};

const config: { [ot in OperationType | NonOperationType]: OperationTypeConfigBase } = {
  GroundRun: {
    name: 'Ground Run',
    abbreviation: 'Gnd Run',
    key: 'groundrun',
  },
  Maintenance: {
    name: 'Maintenance',
    abbreviation: 'Maint',
    key: 'maintenance',
  },
  RequestorSpecialtyTeam: {
    name: 'Patient Shuttle',
    abbreviation: 'Shuttle',
    key: 'patient-shuttle',
    icon: 'add_location',
  },
  PatientShuttle: {
    name: 'Patient Shuttle',
    abbreviation: 'Shuttle',
    key: 'patient-shuttle',
    icon: 'add_location',
    request: {
      initialDestination: WaypointType.PatientPickupRendezvous,
      targetDestination: WaypointType.PatientDropoffRendezvous,
      locations: [
        {
          waypointType: WaypointType.PatientPickupRendezvous,
          formType: 'allOrCustom',
          panel: {},
        },
        {
          waypointType: WaypointType.PatientDropoffRendezvous,
          formType: 'allOrCustom',
          panel: {},
        },
      ],
      formPanels: {
        Requestor: {},
        RequestorSpecialtyTeam: {},
        ChiefComplaint: {},
        PatientInformation: {},
        MissionInformation: { disabled: true },
        PatientClinicalInformation: { disabled: true },
        TransferCenterCallInformation: { disabled: true },
        SafetyQuestions: {},
      },
    },
  },
  NonPatientShuttle: {
    name: 'Non Patient Shuttle',
    abbreviation: 'Shuttle',
    key: 'non-patient-shuttle',
    icon: 'commute',
    request: {
      initialDestination: WaypointType.RendezvousPoint,
      targetDestination: WaypointType.RendezvousPoint,
      locations: [
        {
          waypointType: WaypointType.RendezvousPoint,
          formType: 'allOrCustom',
          panel: {},
        },
        {
          waypointType: WaypointType.RendezvousPoint,
          formType: 'allOrCustom',
          panel: {},
        },
      ],
      formPanels: {
        Requestor: {},
        RequestorSpecialtyTeam: {},
        ChiefComplaint: { disabled: true },
        PatientInformation: { disabled: true },
        MissionInformation: { disabled: true },
        PatientClinicalInformation: { disabled: true },
        TransferCenterCallInformation: { disabled: true },
        SafetyQuestions: { disabled: true },
      },
    },
  },
  PublicRelations: {
    name: 'PR / Demo',
    abbreviation: 'PR',
    key: 'public-relations',
    icon: 'voice_chat',
    request: {
      initialDestination: WaypointType.PublicRelationsSite,
      locations: [
        {
          waypointType: WaypointType.PublicRelationsSite,
          formType: 'allOrCustom',
          panel: {},
        },
      ],
      formPanels: {
        Requestor: {},
        RequestorSpecialtyTeam: {},
        ChiefComplaint: { disabled: true },
        PatientInformation: { disabled: true },
        MissionInformation: { disabled: true },
        PatientClinicalInformation: { disabled: true },
        TransferCenterCallInformation: { disabled: true },
        SafetyQuestions: {},
      },
    },
  },
  Reposition: {
    name: 'Reposition',
    abbreviation: 'Repo',
    key: 'reposition',
  },
  SearchAndAssist: {
    name: 'Search and Assist',
    abbreviation: 'SAA',
    key: 'search-and-assist',
    icon: 'map',
    request: {
      initialDestination: WaypointType.SceneLocation,
      targetDestination: WaypointType.ReceivingFacility,
      locations: [
        {
          waypointType: WaypointType.SceneLocation,
          formType: 'allOrCustom',
          panel: {},
        },
        {
          waypointType: WaypointType.ReceivingFacility,
          formType: 'medical',
          panel: { collapsed: true },
        },
      ],
      formPanels: {
        Requestor: {},
        RequestorSpecialtyTeam: {},
        ChiefComplaint: {},
        PatientInformation: { collapsed: true },
        MissionInformation: { disabled: true },
        PatientClinicalInformation: { disabled: true },
        TransferCenterCallInformation: { disabled: true },
        SafetyQuestions: {},
      },
    },
  },
  Training: { name: 'Training', abbreviation: 'Training', key: 'training' },
  Transfer: {
    name: 'Transfer',
    abbreviation: 'IFT',
    key: 'transfer',
    icon: 'domain',
    request: {
      initialDestination: WaypointType.SendingFacility,
      targetDestination: WaypointType.ReceivingFacility,
      locations: [
        {
          waypointType: WaypointType.SendingFacility,
          formType: 'medical',
          panel: {},
        },
        {
          waypointType: WaypointType.ReceivingFacility,
          formType: 'medical',
          panel: {},
        },
      ],
      formPanels: {
        Requestor: {},
        RequestorSpecialtyTeam: {},
        ChiefComplaint: {},
        PatientInformation: {},
        MissionInformation: { disabled: true },
        PatientClinicalInformation: { disabled: true },
        TransferCenterCallInformation: { disabled: true },
        SafetyQuestions: {},
      },
    },
  },
  TransferCenter: {
    name: 'Transfer center',
    abbreviation: 'TXF',
    key: 'transfer-center',
    icon: 'location_city',
    request: {
      initialDestination: WaypointType.SendingFacility,
      targetDestination: WaypointType.ReceivingFacility,
      locations: [
        {
          waypointType: WaypointType.SendingFacility,
          formType: 'medical',
          panel: {},
        },
        {
          waypointType: WaypointType.ReceivingFacility,
          formType: 'medical',
          panel: {},
        },
      ],
      formPanels: {
        Requestor: {},
        RequestorSpecialtyTeam: { disabled: true },
        ChiefComplaint: {},
        PatientInformation: {},
        PatientClinicalInformation: {},
        TransferCenterCallInformation: {},
        MissionInformation: { disabled: true },
        SafetyQuestions: { disabled: true },
      },
    },
  },
  SceneCall: {
    name: 'Scene Call',
    abbreviation: 'Scene',
    key: 'scene-call',
    icon: 'local_hospital',
    request: {
      initialDestination: WaypointType.SceneLocation,
      targetDestination: WaypointType.ReceivingFacility,
      locations: [
        {
          waypointType: WaypointType.SceneLocation,
          formType: 'allOrCustom',
          panel: {},
        },
        {
          waypointType: WaypointType.ReceivingFacility,
          formType: 'medical',
          panel: { collapsed: true },
        },
      ],
      formPanels: {
        Requestor: {},
        RequestorSpecialtyTeam: {},
        ChiefComplaint: {},
        PatientInformation: { collapsed: true },
        MissionInformation: { disabled: true },
        PatientClinicalInformation: { disabled: true },
        TransferCenterCallInformation: { disabled: true },
        SafetyQuestions: {},
      },
    },
  },
  Residence: {
    name: 'Residence',
    abbreviation: 'RES',
    key: 'residence',
    icon: 'house',
    request: {
      initialDestination: WaypointType.SendingFacility,
      targetDestination: WaypointType.PatientDropoffRendezvous,
      locations: [
        {
          waypointType: WaypointType.SendingFacility,
          formType: 'medical',
          panel: {},
        },
        {
          waypointType: WaypointType.PatientDropoffRendezvous,
          formType: 'allOrCustom',
          panel: {},
        },
      ],
      formPanels: {
        Requestor: {},
        RequestorSpecialtyTeam: {},
        ChiefComplaint: { disabled: true },
        PatientInformation: { collapsed: true },
        MissionInformation: { disabled: true },
        PatientClinicalInformation: { disabled: true },
        TransferCenterCallInformation: { disabled: true },
        SafetyQuestions: {},
      },
    },
  },
  PublicUtility: {
    name: 'Public Utility Operation',
    abbreviation: 'PUO',
    key: 'public-utility-operation',
    icon: 'public',
    request: {
      initialDestination: WaypointType.SceneLocation,
      locations: [
        {
          waypointType: WaypointType.SceneLocation,
          formType: 'allOrCustom',
          panel: {},
        },
      ],
      formPanels: {
        Requestor: {},
        RequestorSpecialtyTeam: {},
        ChiefComplaint: { disabled: true },
        PatientInformation: { disabled: true },
        MissionInformation: {},
        PatientClinicalInformation: { disabled: true },
        TransferCenterCallInformation: { disabled: true },
        SafetyQuestions: {},
      },
    },
  },
};

export const operationTypeConfig: {
  [ot in OperationType | NonOperationType]: OperationTypeConfig;
} = Object.entries(config).reduce((acc, typeConfig) => {
  const [type, cfg] = typeConfig;
  acc[type] = { type, ...cfg };
  return acc;
}, {} as any);

export const requestOperationTypes = Object.values(operationTypeConfig)
  .filter((ot) => ot.request != null)
  .map((c) => {
    return {
      ...c,
      request: c.request,
    };
  });
